import {
  ICommentsAPI,
  ICommercialCardAPI,
  ICommercialCardCompoentsProps,
  ILoginMotivationPopupAPI,
  IUserInfoAPI,
  TCommercialCardTracking,
} from '@cian/api-models/search-card-fragment/v1/get-commercial-card.props';
import {
  ITrackFirstMessageSentWithProductsParams,
  trackAddCommentClick,
  trackAddressClick,
  trackDocLinkClicked,
  trackFirstMessageClick,
  trackFirstMessageOpen,
  trackFirstMessageSentWithProducts,
  trackGoToMessages,
  trackHidePrintButtonClick,
  trackingCreate,
  trackingEnter,
  trackingShowPopup,
  trackMapClosed,
  trackMapOpened,
  trackMenuOpened,
  trackMultiOfferClick,
  trackPDFLinkClicked,
  trackReportPopupOpened,
  trackShowAllIdenticalOffers,
} from './tracking';
import { IOfferCardCreatorProps } from './helpers';
import { EFeedbackComplaint, IComplaintFeedbackBody } from '../../state/offer_card/feedback_complaint';

export interface IPrepareCommercialCardProps {
  onFavoriteChange(isFavorite: boolean): void;
  onPageJumped(label?: string): void;
  onPopupClose(): void;
  onPopupOpen(): void;
  onPhoneOpened(): void;
  onHideOffer(): void;
  onPhotoSlideChangedOnce(): void;
  mortgageBanner?: React.ReactNode | undefined;
  trackTradePopupShow(): void;
  trackTradePopupClick(): void;
  analyticsPopup: {
    onButtonClick(offerId: number): void;
  };
}

export interface ICommercialCardComplaintsHelpers {
  sendComplaintFeedback(body: IComplaintFeedbackBody): void;
  statusChanger(status: EFeedbackComplaint): void;
  complaintsFormStatus: EFeedbackComplaint;
}

export interface ICommercialCardComponentExtension {
  reportPopup: {
    onComplaintSent(offerId: number, name: string, message?: string): void;
  };
  complaintsHelpers: ICommercialCardComplaintsHelpers;
  analyticsPopup: {
    onButtonClick(offerId: number): void;
  };
  onOfferCardClick?(): void;
}

export function prepareCommercialCardComponentsProps(
  creatorProps: IOfferCardCreatorProps,
  prepareProps: IPrepareCommercialCardProps,
  params: ITrackFirstMessageSentWithProductsParams,
): ICommercialCardCompoentsProps & ICommercialCardComponentExtension {
  const {
    closePopup,
    commentingBlockedOffers,
    commentingBlockedAgents,
    commentingErroneousOffers,
    commentingErroneousAgents,
    onAgentRatingChanged,
    onOfferCommentsChanged,
    shownId,
    ratingBlockedAgents,
    user,
    favoritesLimitForUnauthUser,
    onComplaintSent,
    sendComplaintFeedback,
    statusChanger,
    complaintsFormStatus,
    onOfferCardClick,
  } = creatorProps;

  const {
    mortgageBanner,
    onFavoriteChange,
    onPageJumped,
    onPopupClose,
    onPopupOpen,
    onPhoneOpened,
    onHideOffer,
    onPhotoSlideChangedOnce,
    trackTradePopupShow,
    trackTradePopupClick,
    analyticsPopup,
  } = prepareProps;

  const comments: ICommentsAPI = {
    commentingBlockedOffers,
    commentingBlockedAgents,
    commentingErroneousOffers,
    commentingErroneousAgents,
    isAuthenticated: user.isAuthenticated,
    onOfferCommentsChanged,
  };

  const loginMotivationPopup: ILoginMotivationPopupAPI = {
    closePopup,
    shownId,
  };

  const card: ICommercialCardAPI = {
    onPhotoSlideChangedOnce,
    onMapOpened: trackMapOpened,
    onMapClosed: trackMapClosed,
    onMenuOpened: trackMenuOpened,
    onPDFLinkClicked: trackPDFLinkClicked,
    onDocLinkClicked: trackDocLinkClicked,
    onFavoriteChange,
    onPageJumped,
    onPhoneOpened,
    onHideOffer,
  };

  const userInfo: IUserInfoAPI = {
    onAgentRatingChanged,
    onPopupClose,
    onPopupOpen,
    ratingBlockedAgents,
    userIsAuthenticated: user.isAuthenticated,
  };

  const { position, offer, jsonQuery } = params;
  const tracking: TCommercialCardTracking = {
    trackAddCommentClick,
    trackAddressClick,
    trackFirstMessageClick: (trackingLabel: string) =>
      trackFirstMessageClick({ position, offer, jsonQuery }, trackingLabel),
    trackFirstMessageSent: trackFirstMessageSentWithProducts(params),
    trackFirstMessageOpen: (trackingLabel: string) =>
      trackFirstMessageOpen({ position, offer, jsonQuery }, trackingLabel),
    trackGoToMessages,
    trackHidePrintButtonClick,
    trackingCreate,
    trackingEnter,
    trackingShowPopup,
    trackMultiOfferClick,
    trackAllDuplicatesClick: () => trackShowAllIdenticalOffers({ position, offer, jsonQuery }),
    trackReportPopupOpened,
    trackTradePopupShow,
    trackTradePopupClick,
  };

  return {
    card,
    comments,
    favoritesLimitForUnauthUser,
    loginMotivationPopup,
    mortgageBanner,
    userInfo,
    tracking,
    reportPopup: {
      onComplaintSent,
    },
    complaintsHelpers: {
      sendComplaintFeedback,
      statusChanger,
      complaintsFormStatus,
    },
    analyticsPopup,
    onOfferCardClick,
  };
}
